import Loader from "@components/layouts/Loader";
import { Button } from "@kirvanobr/button";
import { api } from "@services/apiClient";
import { queryClient } from "@services/queryClient";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-cropper";
import { UploadCloud, ZoomIn, ZoomOut } from "react-feather";
import { useForm } from "react-hook-form";
import ImageUploading, { ImageListType, ImageType } from "react-images-uploading";
import InputFeedback from "../InputFeedback";
import { Text } from "../Text";
import { useToastContext } from "../Toast/Context";
import { Buttons, CropperContainer, Wrapper, ZoomWrapper } from "./styles";

interface ImageCropProps {
  uuid?: string;
  path?: string;
  initialAspectRatio?: number;
  aspectRatio?: number;
  imagesDefault?: ImageType[] | null;
  closeModal?: () => void;
  onSubmit?: (cropper: any) => Promise<void>;
  onCancel?: () => void;
}

export function ImageCrop({
  uuid,
  path,
  imagesDefault,
  initialAspectRatio = 5 / 5,
  aspectRatio = 5 / 5,
  closeModal,
  onSubmit,
  onCancel,
}: ImageCropProps) {
  const cropperRef = useRef<HTMLImageElement>(null);
  const [cropper, setCropper] = useState<any>();
  const [loading, setLoading] = useState(false);
  const toast = useToastContext();

  const [images, setImages] = useState<ImageType[]>([]);

  type queriesDataType = {
    queryKey: string[];
  };

  type setDataType = {
    url: string;
    method: string;
    queries: queriesDataType[];
  };

  const [data, setData] = useState<setDataType>({
    url: "",
    method: "",
    queries: [],
  });
  const maxNumber = 1;

  useEffect(() => {
    if (path == "product") {
      setData({
        url: `products/${uuid}/cover-picture?=${new Date().getTime()}`,
        method: "post",
        queries: [
          { queryKey: ["/product", uuid ?? ""] },
          { queryKey: ["/products"] },
          { queryKey: ["/affiliates-settings"] },
          { queryKey: ["/last-affiliates-settings"] },
        ],
      });
    } else if (path == "profile") {
      setData({
        url: `users/update-photo`,
        method: "put",
        queries: [{ queryKey: ["/users", "/me"] }, { queryKey: ["/users"] }],
      });
    }
  }, [path, setData, uuid]);

  useEffect(() => {
    setImages(imagesDefault as ImageType[]);
  }, [imagesDefault]);

  const { handleSubmit } = useForm();

  const onChange = (imageList: ImageListType) => {
    setImages(imageList as ImageType[]);
  };

  const zoomInCrop = () => {
    cropper.zoom(0.5);
  };

  const zoomOutCrop = () => {
    cropper.zoom(-0.5);
  };

  // will define and save the crop image
  const upload = async () => {
    if (onSubmit) return onSubmit(cropper);
    if (typeof cropper !== "undefined") {
      setLoading(true);
      cropper.getCroppedCanvas().toBlob(async (blob: Blob | null) => {
        try {
          const file = new File([blob!], "logo.png", { type: "image/png" });

          await api[data?.method === "post" ? "post" : "put"](
            data.url,
            {
              file: file,
            },
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          data.queries.forEach(query => {
            queryClient.refetchQueries(query);
          });

          if (closeModal) {
            closeModal();
          }

          toast({ type: "success", title: "Imagem atualizada com sucesso!" });
        } catch (error: any) {
          toast({
            type: "error",
            title: error?.response?.data.message || "Ops! Algo saiu errado, por favor tente novamente.",
          });
        } finally {
          setLoading(false);
        }
      });
    }
  };

  const btnCancel = (onImageRemoveAll: any): any => {
    if (onCancel) return onCancel();
    if (onImageRemoveAll) onImageRemoveAll();
  };

  return (
    <Wrapper onSubmit={handleSubmit(upload)}>
      <Loader loading={loading} />

      <ImageUploading
        value={images}
        onChange={onChange}
        maxNumber={maxNumber}
        maxFileSize={10000000}
        acceptType={["jpg", "jpeg", "png"]}
      >
        {({ imageList, onImageUpload, onImageRemoveAll, isDragging, dragProps, errors }) => (
          <>
            <div className="upload__image-wrapper">
              {!!imageList[0] ? (
                <>
                  <CropperContainer>
                    <Cropper
                      src={imageList[0].dataURL}
                      name={"file"}
                      className="cropper"
                      style={{
                        height: 500,
                        width: "100%",
                      }}
                      initialAspectRatio={initialAspectRatio}
                      aspectRatio={aspectRatio}
                      viewMode={0}
                      background={false}
                      responsive
                      guides
                      minCropBoxHeight={50}
                      minCropBoxWidth={50}
                      ref={cropperRef}
                      onInitialized={instance => {
                        setCropper(instance);
                      }}
                    />
                  </CropperContainer>

                  <ZoomWrapper>
                    <Button type="button" size="sm" radius="full" color="primary" isIconOnly onClick={zoomOutCrop}>
                      <ZoomOut />
                    </Button>

                    <Button type="button" size="sm" radius="full" color="primary" isIconOnly onClick={zoomInCrop}>
                      <ZoomIn />
                    </Button>
                  </ZoomWrapper>

                  <Buttons>
                    <Button variant="auxiliary" fullWidth onClick={() => btnCancel(() => onImageRemoveAll())}>
                      Descartar
                    </Button>

                    <Button fullWidth type="submit">
                      Confirmar
                    </Button>
                  </Buttons>
                </>
              ) : (
                <button
                  id={"image-uploader"}
                  type="button"
                  className="click-drop-button"
                  style={isDragging ? { color: "red" } : undefined}
                  onClick={onImageUpload}
                  {...dragProps}
                >
                  <UploadCloud size={40} fontWeight={500} />
                  <Text>
                    Arraste e solte ou <span>busque</span>
                  </Text>
                  <Text size={"small"}>Formatos: JPEG, PNG</Text>
                </button>
              )}
            </div>

            {errors && (
              <div>
                {errors.maxNumber && <InputFeedback type="error" message={"Selecione apenas uma imagem."} />}
                {errors.acceptType && (
                  <InputFeedback type="error" message={"Formato inválido. Formatos aceitos: jpg, jpeg e png."} />
                )}
                {errors.maxFileSize && <InputFeedback type="error" message={"A imagem deve ter no máximo 500kb."} />}
              </div>
            )}
          </>
        )}
      </ImageUploading>
    </Wrapper>
  );
}
